/**
 * `IProjectionServiceClient` — typed client for the PBC-operated PHG
 * projection-service backend.
 *
 * The PHG web client is the consumer side of DEV3's
 * `@shn/phg-projection-service` integration interfaces:
 *
 *   - `IPatientKeyCustodian` — store / fetch wrapped KEK + DEK
 *     ciphertext (the projection-service is a ciphertext custodian
 *     per the integration interface; the web client wraps + unwraps
 *     client-side).
 *   - `IPatientProjectionStore` — store + read encrypted projection
 *     records by subject; cryptographic-erasure invocation per
 *     ADR-024 §2.7 three-outcome split (erase both / holder-retained
 *     only / PHG-resident only).
 *   - `IFederatedQueryAggregationConsumer` (PR #99) — drain pending
 *     federated-query aggregations buffered SHT-side per Issue #51
 *     Pattern B for client-session pull-render-encrypt-write.
 *   - `IAuditChainPullConsumer` (proposed; this PR) — drain pending
 *     audit-chain events buffered SHT-side per Issue #51 Pattern B.
 *     Producer side is DEV3 claim-queue item 4; the consumer-side
 *     contract here mirrors `IFederatedQueryAggregationConsumer`'s
 *     drain shape and is forward-compatible per Channel-2 ping on
 *     Issue #51 (this PR).
 *   - Rendering-taxonomy artifact fetch — versioned editorial
 *     artifact (Issue #41 ownership; v0 ships engineering baseline)
 *     for client-side rendering per Issue #51 closing.
 *
 * **Pattern B no-parallel-surface posture.** Per Issue #51 ratification,
 * patient-comprehensible rendering happens client-side. The previous
 * PR #39 / PR #107 scaffold's `fetchRenderedProjection(recordId,
 * locale)` server-side rendering surface is removed in this PR — it
 * was a Pattern A scaffold artifact. Read-time rendering uses
 * `IRenderingTaxonomy` from `@shn/phg-client-web/projection`,
 * applied to decrypted `PatientProjectionContent` envelopes pulled
 * via `queryProjectionRecords`.
 *
 * **No cleartext keys flow through this client.** Every key-material
 * field this client exchanges with the projection-service is
 * ciphertext (wrapped under a key SHT does not hold).
 */
import { DEFAULT_PBKDF2_ITERATIONS, TIER_COOL_OFF_SECONDS, TIER_REQUIRED_LAYERS, } from '../recovery/canonical-constants.js';
// ---------- Mock implementation (test + dev) ----------
/**
 * In-memory stub mirroring substrate-side scaffold semantics for
 * tests + dev. Composite-key tenant + subject scoping; cross-tenant
 * violations surface as thrown errors per ADR-020 §5.5 Layer 3.
 *
 * Production posture: real HTTP client at the joint DEV3+DEV5
 * production-pipeline PR (per Issue #51 closing follow-on).
 */
export class MockProjectionServiceClient {
    kekCounter = 0;
    dekCounter = 0;
    keks = new Map();
    deks = new Map();
    projectionRecords = new Map();
    pendingAggregationsBySubject = new Map();
    pendingAuditEventsBySubject = new Map();
    renderingTaxonomyArtifact = null;
    async storeWrappedKek(req) {
        const kekId = `kek_mock_${++this.kekCounter}`;
        const material = {
            kekId,
            kekCiphertext: req.wrappedKekBytes,
            kekVersion: req.kekVersion,
        };
        this.keks.set(scopeKey(req.tenantId, req.subjectPciHash, kekId), material);
        return material;
    }
    async fetchWrappedKek(req) {
        const m = this.keks.get(scopeKey(req.tenantId, req.subjectPciHash, req.kekId));
        return m ?? null;
    }
    async storeWrappedDek(req) {
        const dekId = `dek_mock_${++this.dekCounter}`;
        const wrapped = {
            dekId,
            kekId: req.kekId,
            dekCiphertext: req.wrappedDekBytes,
            aad: req.aad,
        };
        this.deks.set(scopeKey(req.tenantId, req.subjectPciHash, dekId), wrapped);
        return wrapped;
    }
    async fetchWrappedDek(req) {
        const w = this.deks.get(scopeKey(req.tenantId, req.subjectPciHash, req.dekId));
        return w ?? null;
    }
    async storePatientProjectionRecord(opts) {
        if (opts.record.tenantId !== opts.tenantId) {
            return { outcome: 'tenant-mismatch' };
        }
        this.projectionRecords.set(opts.record.recordId, opts.record);
        return { outcome: 'ok' };
    }
    async queryProjectionRecords(opts) {
        const records = [];
        for (const r of this.projectionRecords.values()) {
            if (r.subjectPciHash !== opts.request.subjectPciHash)
                continue;
            if (r.tenantId !== opts.tenantId)
                continue;
            if (opts.request.contentCategories !== undefined &&
                opts.request.contentCategories.length > 0 &&
                !opts.request.contentCategories.includes(r.contentCategory)) {
                continue;
            }
            records.push(r);
        }
        // Stable order by projectionAnchoredAt for deterministic tests.
        records.sort((a, b) => a.projectionAnchoredAt < b.projectionAnchoredAt ? -1 : 1);
        return { records };
    }
    async erasePatientProjection(req) {
        if (req.patientAuthorizationToken.length === 0) {
            throw new Error('patientAuthorizationToken required for erasure');
        }
        let removed = 0;
        for (const [k, r] of this.projectionRecords) {
            if (r.subjectPciHash === req.subjectPciHash && r.tenantId === req.tenantId) {
                this.projectionRecords.delete(k);
                removed += 1;
            }
        }
        return { outcome: removed > 0 ? 'ok' : 'not-found' };
    }
    async drainPendingAggregations(req) {
        const k = subjectKey(req.tenantId, req.subjectPciHash);
        const queue = this.pendingAggregationsBySubject.get(k) ?? [];
        this.pendingAggregationsBySubject.delete(k);
        return queue;
    }
    async drainPendingAuditChainEvents(req) {
        const k = subjectKey(req.tenantId, req.subjectPciHash);
        const queue = this.pendingAuditEventsBySubject.get(k) ?? [];
        this.pendingAuditEventsBySubject.delete(k);
        return queue;
    }
    async fetchRenderingTaxonomyArtifact(_req) {
        if (this.renderingTaxonomyArtifact === null) {
            throw new Error('no rendering-taxonomy artifact seeded in MockProjectionServiceClient; call seedRenderingTaxonomyArtifact first');
        }
        return this.renderingTaxonomyArtifact;
    }
    // ---------- Test-double helpers ----------
    /** Seed a stored projection record (e.g., test fixture). */
    seedProjectionRecord(record) {
        this.projectionRecords.set(record.recordId, record);
    }
    /** Seed a pending aggregation for the given subject. */
    seedPendingAggregation(pending) {
        const k = subjectKey(pending.tenantId, pending.subjectPciHash);
        const queue = this.pendingAggregationsBySubject.get(k) ?? [];
        queue.push(pending);
        this.pendingAggregationsBySubject.set(k, queue);
    }
    /** Seed a pending audit-chain event for the given subject. */
    seedPendingAuditChainEvent(pending) {
        const k = subjectKey(pending.tenantId, pending.subjectPciHash);
        const queue = this.pendingAuditEventsBySubject.get(k) ?? [];
        queue.push(pending);
        this.pendingAuditEventsBySubject.set(k, queue);
    }
    /** Seed the rendering-taxonomy artifact returned by `fetchRenderingTaxonomyArtifact`. */
    seedRenderingTaxonomyArtifact(artifact) {
        this.renderingTaxonomyArtifact = artifact;
    }
    // ---------- Aggregation election mocks (PR #150 §3) ----------
    holderInventory = [];
    holderCohortFlags = new Map();
    async fetchHolderInventory(_req) {
        return this.holderInventory;
    }
    async scheduleAggregation(_req) {
        return { initiatedFirstPullAt: new Date().toISOString() };
    }
    async fetchHolderCohortFlags(req) {
        const flags = this.holderCohortFlags.get(req.holderId) ?? [];
        return { holderId: req.holderId, enabledFlags: flags };
    }
    /** Test-double helper: seed holder inventory. */
    seedHolderInventory(inventory) {
        this.holderInventory = inventory;
    }
    /** Test-double helper: seed cohort flags for a holder. */
    seedHolderCohortFlags(holderId, flags) {
        this.holderCohortFlags.set(holderId, flags);
    }
    // ---------- Recovery flow mocks (DEV3 PR #156 §4 wire shape) ----------
    recoverySessionCounter = 0;
    recoverySessions = new Map();
    nextInitiateOutcome = 'ok';
    nextLayerOutcome = { outcome: 'completed' };
    /**
     * Optional override for the per-share material at submitRecoveryLayer
     * outcome=completed. When set, mock returns these fields verbatim
     * instead of deterministic counter-based values. Used by Step 3b
     * §3.4 substantive submitLayer cycle integration tests to inject a
     * real per-share AEAD ciphertext matching test-side
     * (layerEvidenceKey + shareAad) inputs. One-shot — cleared after
     * consumption to preserve isolation between tests.
     */
    nextLayerShareOverride = undefined;
    nextFinalizeOutcome = 'ok';
    /**
     * Optional override for the canonical envelope payload at outcome=ok.
     * When set, `finalizeRecovery` returns these fields verbatim instead of
     * the deterministic counter-based mock values. Used by Step 3b §3.1
     * substantive end-to-end integration tests (memo §9.1 row 5) to inject a
     * real wrapped-PMK envelope matching test-side (shares + passphrase + AAD)
     * inputs.
     */
    nextFinalizeEnvelopeOverride = undefined;
    // ---------- Windowed audit records mock (DEV5-3 wire-mirror) ----------
    seededAuditRecordsWindow = [];
    /**
     * Test seam — pre-seed snapshots returned by `fetchAuditRecordsWindow`.
     * Production wiring is HTTP at the joint DEV3+DEV5 production-pipeline
     * PR; this mock returns the seeded array honoring k-anonymity guard
     * for the institutional consumer profile.
     */
    seedAuditRecordsWindow(snapshots) {
        this.seededAuditRecordsWindow = snapshots;
    }
    async fetchAuditRecordsWindow(req) {
        const filtered = this.seededAuditRecordsWindow.filter((s) => {
            if (s.recordedAt < req.timeRange.from || s.recordedAt > req.timeRange.to)
                return false;
            if (req.operationTypes !== undefined && !req.operationTypes.includes(s.operationType)) {
                return false;
            }
            if (req.tenantScope.kind === 'single-tenant') {
                if (s.subjectPciId !== req.tenantScope.subjectPciId)
                    return false;
                if (req.tenantScope.tenantId !== undefined &&
                    s.tenantId !== undefined &&
                    s.tenantId !== req.tenantScope.tenantId) {
                    return false;
                }
            }
            return true;
        });
        if (req.consumerProfile.kind === 'initiative-15-institutional' &&
            filtered.length < req.consumerProfile.minWindowSize) {
            return {
                page: { records: [] },
                anonymityGuard: {
                    suppressed: true,
                    reason: 'window-below-min-size',
                    observedWindowSize: filtered.length,
                    minWindowSize: req.consumerProfile.minWindowSize,
                },
            };
        }
        const limit = req.limit ?? filtered.length;
        const records = filtered.slice(0, limit);
        const nextCursor = filtered.length > limit ? `mock-cursor-${limit}` : undefined;
        return {
            page: nextCursor !== undefined ? { records, nextCursor } : { records },
        };
    }
    // ---------- Patient ops-log mock (Account v0 home §2.4 wire-mirror) ----------
    /**
     * Mock storage: subjectPciId → entries. Per Account v0 §2.4 substrate-side
     * canonical home discipline, `PatientOpsLogEntry` itself is already patient-
     * scoped at the request boundary (subject is in the request, not the entry);
     * mock storage carries subjectPciId externally for test-fixture filtering.
     */
    patientOpsLogBySubject = new Map();
    /**
     * Test seam — pre-seed entries for a given subject. Production wiring is
     * HTTP at the joint DEV3+DEV5 production-pipeline PR; this mock returns
     * the seeded array filtered by optional `tenantId`, sorted descending by
     * `recordedAt`, paginated by simple mock-cursor.
     */
    seedPatientOpsLog(subjectPciId, entries) {
        this.patientOpsLogBySubject.set(subjectPciId, entries);
    }
    async getPatientOpsLog(req) {
        const seeded = this.patientOpsLogBySubject.get(req.subjectPciId) ?? [];
        const filtered = seeded
            .filter((e) => req.tenantId === undefined || e.tenantId === req.tenantId)
            .slice()
            .sort((a, b) => {
            const primary = b.recordedAt.localeCompare(a.recordedAt);
            return primary !== 0 ? primary : b.recordId.localeCompare(a.recordId);
        });
        const limit = req.limit ?? filtered.length;
        const startIdx = req.cursor ? Number.parseInt(req.cursor.replace('mock-ops-cursor-', ''), 10) : 0;
        const slice = filtered.slice(startIdx, startIdx + limit);
        const nextCursor = startIdx + limit < filtered.length ? `mock-ops-cursor-${startIdx + limit}` : undefined;
        // Conditional spread for the optional `nextCursor` slot
        // (exactOptionalPropertyTypes): omit when there is no more page rather
        // than emitting `nextCursor: undefined`.
        return {
            entries: slice,
            ...(nextCursor !== undefined ? { nextCursor } : {}),
        };
    }
    // ---------- SHA v0 recovery-setup ceremony mock (PR #430 §3.2 + §6 wire-mirror) ----------
    /**
     * Mock ceremony state. Mirrors substrate-side `IRecoverySetupCoordinator`
     * state-machine semantics: in-progress → complete (after threshold-met
     * + finalize); duplicate-element + unknown-setup-id refusals.
     */
    recoverySetupCeremonies = new Map();
    recoverySetupActiveBySubject = new Map();
    recoverySetupCounter = 0;
    /**
     * Test seam — pre-set the elements-required for a tier. Mirrors
     * substrate-side `TIER_REQUIRED_LAYERS` derivation. v0 mock defaults
     * to {low: 2, medium: 3, high: 3, highest: 4} parallel to substrate-side
     * canonical-constants.
     */
    mockTierRequiredElements = {
        'low-risk': 2,
        'medium-risk': 3,
        'high-risk': 3,
        'highest-risk': 4,
    };
    /** Test seam — override tier-required-elements for test scenarios. */
    setMockTierRequiredElements(map) {
        this.mockTierRequiredElements = { ...map };
    }
    async initiateRecoverySetup(req) {
        const existingActive = this.recoverySetupActiveBySubject.get(req.subjectPciHash);
        if (existingActive !== undefined) {
            const existing = this.recoverySetupCeremonies.get(existingActive);
            if (existing && !existing.finalized) {
                return { outcome: 'already-active-setup' };
            }
        }
        const setupId = `recsetup_mock_${++this.recoverySetupCounter}`;
        const elementsRequired = this.mockTierRequiredElements[req.tier];
        this.recoverySetupCeremonies.set(setupId, {
            setupId,
            subjectPciHash: req.subjectPciHash,
            tier: req.tier,
            elementsRequired,
            committedKinds: new Set(),
            finalized: false,
        });
        this.recoverySetupActiveBySubject.set(req.subjectPciHash, setupId);
        return {
            outcome: 'ok',
            setupId,
            elementsRequired,
            status: {
                kind: 'in-progress',
                tier: req.tier,
                elementsCommitted: 0,
                elementsRequired,
            },
        };
    }
    async submitRecoveryElement(req) {
        const ceremony = this.recoverySetupCeremonies.get(req.setupId);
        if (!ceremony)
            return { outcome: 'unknown-setup-id' };
        if (ceremony.finalized)
            return { outcome: 'already-complete' };
        if (ceremony.committedKinds.has(req.element.kind)) {
            return { outcome: 'duplicate-element' };
        }
        ceremony.committedKinds.add(req.element.kind);
        return {
            outcome: 'ok',
            status: {
                kind: 'in-progress',
                tier: ceremony.tier,
                elementsCommitted: ceremony.committedKinds.size,
                elementsRequired: ceremony.elementsRequired,
            },
        };
    }
    async finalizeRecoverySetup(req) {
        const ceremony = this.recoverySetupCeremonies.get(req.setupId);
        if (!ceremony)
            return { outcome: 'unknown-setup-id' };
        if (ceremony.finalized)
            return { outcome: 'already-finalized' };
        if (ceremony.committedKinds.size < ceremony.elementsRequired) {
            return { outcome: 'incomplete-elements' };
        }
        const recoveryEnvelopeId = `recenv_mock_${this.recoverySetupCounter}`;
        ceremony.finalized = true;
        ceremony.recoveryEnvelopeId = recoveryEnvelopeId;
        this.recoverySetupActiveBySubject.delete(ceremony.subjectPciHash);
        return {
            outcome: 'ok',
            status: { kind: 'complete', tier: ceremony.tier, recoveryEnvelopeId },
            recoveryEnvelopeId,
        };
    }
    async getRecoverySetupStatus(req) {
        const ceremony = this.recoverySetupCeremonies.get(req.setupId);
        if (!ceremony)
            return { outcome: 'unknown-setup-id' };
        if (ceremony.finalized && ceremony.recoveryEnvelopeId !== undefined) {
            return {
                outcome: 'ok',
                status: {
                    kind: 'complete',
                    tier: ceremony.tier,
                    recoveryEnvelopeId: ceremony.recoveryEnvelopeId,
                },
            };
        }
        return {
            outcome: 'ok',
            status: {
                kind: 'in-progress',
                tier: ceremony.tier,
                elementsCommitted: ceremony.committedKinds.size,
                elementsRequired: ceremony.elementsRequired,
            },
        };
    }
    async initiateRecovery(req) {
        if (this.nextInitiateOutcome !== 'ok') {
            return {
                sessionId: '',
                requiredLayers: [],
                coolOffSeconds: 0,
                outcome: this.nextInitiateOutcome,
            };
        }
        const sessionId = `rec_mock_${++this.recoverySessionCounter}`;
        // Phase B-cryptographic-mechanism canonical-constants composition
        // per [PR #290](https://github.com/SmartHealthNetwork/SHN2026/pull/290).
        // Mock now consumes the client-side canonical mirror at
        // `src/recovery/canonical-constants.ts` instead of inline duplicates.
        // Drift detection enforced via
        // `test/recovery/canonical-constants-binding.test.ts`.
        const requiredLayers = TIER_REQUIRED_LAYERS[req.tier];
        const coolOffSeconds = TIER_COOL_OFF_SECONDS[req.tier];
        const recoveryEnvelopeId = `01HZX0RECOVERYENV${String(this.recoverySessionCounter).padStart(13, '0')}`;
        this.recoverySessions.set(sessionId, {
            subjectPciHash: req.subjectPciHash,
            tier: req.tier,
            requiredLayers,
            coolOffSeconds,
            completedLayers: new Set(),
            recoveryEnvelopeId,
            shareIndexCursor: 0,
        });
        return { sessionId, requiredLayers, coolOffSeconds, outcome: 'ok' };
    }
    async submitRecoveryLayer(req) {
        const session = this.recoverySessions.get(req.sessionId);
        if (session === undefined) {
            return { outcome: 'rejected', reason: 'generic' };
        }
        if (this.nextLayerOutcome.outcome !== 'completed') {
            return this.nextLayerOutcome;
        }
        session.completedLayers.add(req.layerKind);
        // Per Step 3b memo §3.4 Step 2 worked example: substrate emits
        // recovery share material on outcome=completed for layers carrying
        // share material. Mock auto-populates deterministic per-(session,
        // share-position) values keyed off recoverySessionCounter for test
        // reproducibility. Per-test override via
        // `seedNextRecoveryLayerShareOverride` (one-shot) when tests need
        // to inject real AEAD ciphertext matching test-side (layerEvidenceKey
        // + shareAad) inputs.
        session.shareIndexCursor += 1;
        const override = this.nextLayerShareOverride;
        this.nextLayerShareOverride = undefined;
        const shareIndex = override?.shareIndex ?? session.shareIndexCursor;
        const recoveryEnvelopeId = override?.recoveryEnvelopeId ?? session.recoveryEnvelopeId;
        const recoveryShareCiphertext = override?.recoveryShareCiphertext ??
            new Uint8Array([shareIndex, 0xa1, 0xa2, 0xa3]);
        return {
            ...this.nextLayerOutcome,
            recoveryShareCiphertext,
            shareIndex,
            recoveryEnvelopeId,
        };
    }
    async finalizeRecovery(req) {
        const session = this.recoverySessions.get(req.sessionId);
        if (session === undefined) {
            return { outcome: 'generic' };
        }
        if (this.nextFinalizeOutcome !== 'ok') {
            return { outcome: this.nextFinalizeOutcome };
        }
        const allLayersComplete = session.requiredLayers.every((l) => session.completedLayers.has(l));
        if (!allLayersComplete) {
            return { outcome: 'insufficient-layers' };
        }
        // Per `nextFinalizeEnvelopeOverride` test seam: when set, return the
        // overridden envelope payload. Used by Step 3b §3.1 substantive end-to-
        // end integration tests to inject a real wrapped-PMK envelope matching
        // test-side (shares + passphrase + AAD) inputs. Override is one-shot —
        // cleared after consumption to preserve isolation between tests.
        const override = this.nextFinalizeEnvelopeOverride;
        this.nextFinalizeEnvelopeOverride = undefined;
        return {
            outcome: 'ok',
            wrappedPmkCiphertext: override?.wrappedPmkCiphertext ?? new Uint8Array([1, 2, 3, 4]), // placeholder mock ciphertext
            recoveryPassphraseSalt: override?.recoveryPassphraseSalt ?? new Uint8Array(16),
            // Phase B canonical: pbkdf2Iterations sourced from canonical
            // mirror per PR #290. Was inline 600_000; now structurally
            // bound to canonical via DEFAULT_PBKDF2_ITERATIONS import.
            pbkdf2Iterations: override?.pbkdf2Iterations ?? DEFAULT_PBKDF2_ITERATIONS,
            // Per-envelope unique identifier; substrate-side emits via
            // RecoveryPassphraseEnvelope.recoveryEnvelopeId per PR #323.
            // Mock returns the per-session id assigned at initiateRecovery so
            // it stays stable across the session's submitLayer + finalize
            // surfaces (cross-share + cross-envelope binding per Step 3b memo
            // §3.4 Step 2 worked example), or override-supplied when
            // registration-time envelope construction binds to a known id.
            recoveryEnvelopeId: override?.recoveryEnvelopeId ?? session.recoveryEnvelopeId,
            coolOffUntil: override?.coolOffUntil ??
                new Date(Date.now() + session.coolOffSeconds * 1000).toISOString(),
        };
    }
    async getCoolOffStatus(req) {
        const session = this.recoverySessions.get(req.sessionId);
        if (session === undefined) {
            return {
                coolOffActive: false,
                secondsRemaining: 0,
                currentlyAvailableTier: 'low-risk',
            };
        }
        return {
            coolOffActive: false,
            secondsRemaining: 0,
            currentlyAvailableTier: session.tier,
        };
    }
    async submitTrustedOtherAttestation(_req) {
        return { outcome: 'ok' };
    }
    /** Test-double helper: configure next initiateRecovery outcome. */
    seedNextRecoveryInitiateOutcome(outcome) {
        this.nextInitiateOutcome = outcome;
    }
    /** Test-double helper: configure next submitRecoveryLayer response. */
    seedNextRecoveryLayerResponse(response) {
        this.nextLayerOutcome = response;
    }
    /** Test-double helper: configure next finalizeRecovery outcome. */
    seedNextRecoveryFinalizeOutcome(outcome) {
        this.nextFinalizeOutcome = outcome;
    }
    /**
     * Test-double helper: override the canonical envelope payload for the
     * next `finalizeRecovery` call at outcome=ok. One-shot; cleared after
     * consumption. Use to inject a real registration-time-constructed
     * wrapped-PMK envelope matching test-side (shares + passphrase + AAD)
     * inputs per `recovery_flow_phase_b_step_3b_design.md` §9.1 row 5
     * end-to-end integration test extension scope.
     *
     * Caller MUST also ensure `nextFinalizeOutcome === 'ok'` (default) and
     * the recovery session has all required layers completed for the
     * override to take effect.
     */
    seedNextRecoveryFinalizeEnvelopeOverride(override) {
        this.nextFinalizeEnvelopeOverride = override;
    }
    /**
     * Test-double helper: override the per-share material at the next
     * `submitRecoveryLayer` call at outcome=completed. One-shot; cleared
     * after consumption. Use to inject a real per-share AEAD ciphertext
     * matching test-side (layerEvidenceKey + shareAad) inputs per
     * `recovery_flow_phase_b_step_3b_design.md` §3.4 substantive
     * submitLayer cycle integration test extension scope.
     *
     * Caller MUST also ensure `nextLayerOutcome.outcome === 'completed'`
     * (default) for the override to take effect.
     */
    seedNextRecoveryLayerShareOverride(override) {
        this.nextLayerShareOverride = override;
    }
    // ---------- InclusionProofAttestation client-side mirror (PR #544 design memo) ----------
    /**
     * Mock storage: dual-index per substrate-side `IInclusionProofAttestationReader`
     * Layer (3) storage backend dual-index commitment (PR #539 §6 + PR #541
     * §4.1). Per Account v0 §6 doctrinal anchor 1 (NO SHT BACKDOOR):
     * attestations carry hash + anchor + signature metadata only — NO PHI/PII
     * payload (verification orthogonal at `@shn/sdk/audit.verifyInclusionProofAttestation`).
     */
    inclusionProofAttestationsByRecordId = new Map();
    inclusionProofAttestationsByAuditLedgerAnchor = new Map();
    /**
     * Test seam — pre-seed an attestation under an explicit `recordId` index
     * key. Substrate-side persistence keys by `recordId` from the signed
     * record (PR #541 §4.3 + PR #547 InMemory IMPL); the attestation itself
     * carries `attestedRecordHash` + `auditLedgerAnchor` + signature
     * metadata but NO `recordId` field per substrate-types canonical
     * (recordId is part of the canonical bytes hashed into
     * `attestedRecordHash`). This seam mirrors substrate-side dual-index
     * commitment (recordId + auditLedgerAnchor) without conflating with
     * the attestation's hash field. Stores under both indexes.
     *
     * Production wiring is HTTP at the joint DEV3+DEV5 production-pipeline
     * PR (Phase 2 per PR #544 §10 phasing). Per anchored-only-emission
     * semantics (PR #539 §3.3 + PR #541 §4.3): only seed for records
     * actually anchored at writer-side sign time; unseeded recordIds
     * return `undefined` symmetrically.
     */
    seedInclusionProofAttestationByRecordId(recordId, attestation) {
        this.inclusionProofAttestationsByRecordId.set(recordId, attestation);
        this.inclusionProofAttestationsByAuditLedgerAnchor.set(attestation.auditLedgerAnchor, attestation);
    }
    async getInclusionProofAttestationByRecordId(recordId) {
        return this.inclusionProofAttestationsByRecordId.get(recordId);
    }
    async getInclusionProofAttestationsByRecordIds(recordIds) {
        const result = new Map();
        for (const recordId of recordIds) {
            const attestation = this.inclusionProofAttestationsByRecordId.get(recordId);
            if (attestation !== undefined) {
                result.set(recordId, attestation);
            }
        }
        return result;
    }
    async getInclusionProofAttestationByAuditLedgerAnchor(auditLedgerAnchor) {
        return this.inclusionProofAttestationsByAuditLedgerAnchor.get(auditLedgerAnchor);
    }
}
function scopeKey(tenantId, subjectPciHash, innerId) {
    const t = tenantId ?? '__no-tenant__';
    return `${t}::${subjectPciHash}::${innerId}`;
}
function subjectKey(tenantId, subjectPciHash) {
    const t = tenantId ?? '__no-tenant__';
    return `${t}::${subjectPciHash}`;
}
//# sourceMappingURL=projection-service-client.js.map